import { devPortOffset } from "./dev-port";

const AUTH_ROUTES = ["/signin", "/forgot-password", "/signup", "/signup/verify", "/invite"];
const VALID_TABS = [
  "employees",
  "teams",
  "organization",
  "wards",
  "roles",
  "settings",
  "leaves",
  "leave",
  "offsite",
  // "overtime-types" 제거: 인페이지 탭에서 유형·기준 통합 관리 팝업으로 이동했다.
  // 스테일 #overtime-types 해시가 유효 탭으로 파싱돼 빈 본문이 뜨던 문제를 막고,
  // 미등록 탭으로 처리해 기본 탭(employees)으로 폴백시킨다. 진입은 handleLnbSelect가 담당.
  "esign",
  "payroll",
  "scheduler",
  "embed",
  "documents",
  // 근태 현황(attendance)·글로벌 파라미터(parameters)는 switchTab이 해시를 쓰는데
  // 목록에 없어 새로고침 시 employees 로 튕기던 누락 — 등록해 라운드트립을 보장한다.
  "attendance",
  "parameters",
  // 직원 앱 배포 온보딩(MED-795) — 새로고침 라운드트립을 위해 등록
  "onboarding",
  // 예약된 변경(MED-827) — 시트 오토메이션 관리 페이지
  "scheduled-changes",
  // 대화 복구(MED-927 E11) — 조직 시스템 관리자만
  "chat-recovery",
];
const DEFAULT_TAB = "employees";

/** 마지막 방문 탭 저장 키 — 해시 없는 루트 진입 시 이 값으로 복원한다. */
const LAST_TAB_KEY = "admin:lastTab";

function readLastTab(): string {
  try {
    return localStorage.getItem(LAST_TAB_KEY) || "";
  } catch {
    return "";
  }
}

function writeLastTab(value: string) {
  try {
    localStorage.setItem(LAST_TAB_KEY, value);
  } catch {
    // localStorage 불가(사생활 모드 등) — 복원만 포기하고 동작은 유지.
  }
}
const BASE_URL = normalizeBaseUrl(import.meta.env.BASE_URL);

function normalizeBaseUrl(baseUrl: string): string {
  if (!baseUrl || baseUrl === "/") {
    return "/";
  }

  const withLeadingSlash = baseUrl.startsWith("/") ? baseUrl : `/${baseUrl}`;
  return withLeadingSlash.endsWith("/") ? withLeadingSlash : `${withLeadingSlash}/`;
}

function stripBasePath(pathname: string): string {
  if (BASE_URL === "/") {
    return pathname || "/";
  }

  const basePath = BASE_URL.slice(0, -1);
  if (pathname === basePath) {
    return "/";
  }

  if (pathname.startsWith(`${basePath}/`)) {
    return pathname.slice(basePath.length) || "/";
  }

  return pathname || "/";
}

function withBasePath(path: string): string {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  if (BASE_URL === "/") {
    return normalizedPath;
  }

  const basePath = BASE_URL.slice(0, -1);
  if (normalizedPath === "/") {
    return `${basePath}/`;
  }

  return `${basePath}${normalizedPath}`;
}

/**
 * 등록된 경로 외 직접 진입은 안내 페이지(404.html — 404 문구 없는 잘못된 접근 안내)로
 * 통일한다. 흰 화면이나 홈 이동 등 앱마다 다르던 동작을 막는다.
 * 탭은 해시(#employees)로 식별하므로 path 기준으로만 검사하고,
 * 뒤로/앞으로(popstate)는 앱 내 상태 복원이므로 검사하지 않는다.
 */
function isKnownPath(path: string): boolean {
  const normalized = path.length > 1 && path.endsWith("/") ? path.slice(0, -1) : path;
  if (normalized === "/" || normalized === "/index.html" || normalized === "/change-password") {
    return true;
  }
  if (normalized === "/workspaces") {
    return true;
  }
  return AUTH_ROUTES.includes(normalized);
}

export function createRouterStore() {
  // 해시 없는 루트 진입(localhost:4001)은 마지막 방문 탭으로 복원한다.
  // 해시가 있으면(딥링크·새로고침) 해시가 항상 우선.
  const initialHash = window.location.hash || (readLastTab() ? `#${readLastTab()}` : "");
  const parsed = parseHash(initialHash);
  const isDevMode = import.meta.env.DEV;

  return {
    path: stripBasePath(window.location.pathname),
    tab: parsed.tab,
    settingsSub: parsed.settingsSub,
    /**
     * Selected embed key when `tab === "embed"` (e.g., "sheets",
     * "scheduler"). Encoded as `#embed/<key>` in the URL hash so
     * browser refresh (F5) lands back on the same iframe page
     * instead of an empty embed shell. Per 2026-06 user report.
     */
    embedSub: parsed.embedSub,
    /**
     * 팀 관리(organization) 트리의 선택 — `#organization/ward:<id>` · `team:<id>` ·
     * `workspace`. 새로고침해도 고르던 병동이 유지되도록(온보딩 9/16 요청) 해시에 싣는다.
     */
    orgSub: parsed.orgSub,
    /** 간호 스케줄러 선택 — `#scheduler/<wardId>:<yyyy>-<m>` (병동·년·월). */
    schedulerSub: parsed.schedulerSub,
    /** 병동 관리 선택 — `#wards/<wardId>`(병동 상세) · `#wards/all`(전체 병동 개요). 2026-09-22 목록+상세로 바뀌며 추가. */
    wardSub: parsed.wardSub,

    workspaceHref(path: string, devPort: number): string {
      const { protocol, hostname, origin } = window.location;

      if (isDevMode) {
        return `${protocol}//${hostname}:${devPort + devPortOffset}`;
      }
      return `${origin}${path}`;
    },

    init() {
      if (!isKnownPath(this.path)) {
        window.location.replace(withBasePath("/404.html"));
        return;
      }
      // 루트 진입에서 마지막 방문 탭을 복원한 경우 URL 해시에도 반영해
      // 주소창과 화면이 일치하도록 한다 (replaceState — 히스토리 오염 없음).
      if (this.path === "/" && !window.location.hash && this.tab !== DEFAULT_TAB) {
        const sub = currentSub(this);
        const hash = sub ? `#${this.tab}/${sub}` : `#${this.tab}`;
        history.replaceState(null, "", `${withBasePath("/")}${hash}`);
      }
      window.addEventListener("popstate", () => {
        this.path = stripBasePath(window.location.pathname);
        const p = parseHash(window.location.hash);
        this.tab = p.tab;
        this.settingsSub = p.settingsSub;
        this.embedSub = p.embedSub;
        this.orgSub = p.orgSub;
        this.schedulerSub = p.schedulerSub;
        this.wardSub = p.wardSub;
      });
    },

    navigate(to: string) {
      if (to === this.path) {
        return;
      }
      history.pushState(null, "", withBasePath(to));
      this.path = to;
      // 대시보드 루트로 이동(로그인 직후 등)이면 마지막 방문 탭으로 복원 —
      // 루트 진입 시 초기 파싱과 동일한 규칙. 그 외 경로는 해시 그대로 파싱.
      const hash =
        to === "/" && !window.location.hash && readLastTab()
          ? `#${readLastTab()}`
          : window.location.hash;
      const p = parseHash(hash);
      this.tab = p.tab;
      this.settingsSub = p.settingsSub;
      this.embedSub = p.embedSub;
      this.orgSub = p.orgSub;
      this.schedulerSub = p.schedulerSub;
      this.wardSub = p.wardSub;
    },

    /**
     * @param opts.replace 히스토리를 쌓지 않고 현재 항목을 바꾼다 — 같은 화면 안의 선택
     *   (병동 트리 클릭, 스케줄러 월 이동)에 쓴다. 뒤로 가기가 선택 하나하나를 되감으면
     *   탭을 벗어나는 데 클릭 수만큼 걸린다.
     */
    navigateTab(tabId: string, subTab?: string, opts: { replace?: boolean } = {}) {
      const hash = subTab ? `#${tabId}/${subTab}` : `#${tabId}`;
      // 마지막 방문 탭 기록 — 해시 미변경 early-return 보다 먼저 (같은 탭 재클릭도 최신화).
      writeLastTab(subTab ? `${tabId}/${subTab}` : tabId);
      if (window.location.hash === hash) {
        return;
      }
      const url = `${withBasePath("/")}${hash}`;
      if (opts.replace) {
        history.replaceState(null, "", url);
      } else {
        history.pushState(null, "", url);
      }
      this.tab = tabId;
      // Route the sub-tab to the field that matches the host tab so
      // a navigate to `embed/sheets` doesn't pollute `settingsSub`
      // (and vice versa).
      if (subTab) {
        if (tabId === "embed") {
          this.embedSub = subTab;
        } else if (tabId === "settings") {
          this.settingsSub = subTab;
        } else if (tabId === "organization") {
          this.orgSub = subTab;
        } else if (tabId === "scheduler") {
          this.schedulerSub = subTab;
        } else if (tabId === "wards") {
          this.wardSub = subTab;
        }
      }
    },

    handleAuthRedirect(
      isLoading: boolean,
      authenticated: boolean,
      requiresPasswordChange = false,
      accountAuthenticated = false,
    ) {
      if (isLoading) {
        return;
      }

      const isAuthRoute = AUTH_ROUTES.includes(this.path);
      const isChangePwRoute = this.path === "/change-password";
      const isHubRoute = this.path === "/workspaces";

      if (authenticated) {
        // 조직 세션 — 기존 동작 + 허브에 남아있으면 대시보드로
        if (requiresPasswordChange && !isChangePwRoute) {
          this.navigate("/change-password");
        } else if (!requiresPasswordChange && (isChangePwRoute || isAuthRoute || isHubRoute)) {
          this.navigate("/");
        }
        return;
      }

      if (accountAuthenticated) {
        // account 세션 (무소속/다중 소속 회원) — 워크스페이스 허브로.
        // /signup/verify 는 인증 완료 화면을 보여준 뒤 버튼으로 이동하므로 예외.
        if (!(isHubRoute || this.path === "/signup/verify")) {
          this.navigate("/workspaces");
        }
        return;
      }

      if (!isAuthRoute) {
        this.navigate("/signin");
      }
    },
  };
}

interface ParsedHash {
  tab: string;
  settingsSub: string;
  embedSub: string;
  orgSub: string;
  schedulerSub: string;
  wardSub: string;
}

function parseHash(hash: string): ParsedHash {
  const raw = hash.replace("#", "");
  const [tabPart, subPart] = raw.split("/");
  const tab = VALID_TABS.includes(tabPart) ? tabPart : DEFAULT_TAB;
  const settingsSub = tab === "settings" && subPart ? subPart : "org";
  const embedSub = tab === "embed" && subPart ? subPart : "";
  const orgSub = tab === "organization" && subPart ? subPart : "";
  const schedulerSub = tab === "scheduler" && subPart ? subPart : "";
  const wardSub = tab === "wards" && subPart ? subPart : "";
  return { tab, settingsSub, embedSub, orgSub, schedulerSub, wardSub };
}

/** 지금 탭의 하위 값 — 해시를 다시 만들 때 탭별 필드 중 맞는 것 하나를 고른다. */
function currentSub(store: ParsedHash): string {
  switch (store.tab) {
    case "embed":
      return store.embedSub;
    case "settings":
      return store.settingsSub;
    case "organization":
      return store.orgSub;
    case "scheduler":
      return store.schedulerSub;
    case "wards":
      return store.wardSub;
    default:
      return "";
  }
}
